import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { Autocomplete, Button, DialogContent, Divider, Icon, InputAdornment, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import { Box } from '@mui/system';

import useNotify from 'Lib/hooks/useNotify';
import useTrigger from 'Lib/hooks/useTrigger';
import Dialog from 'Lib/muiComponents/dialog/Dialog';
import { initializeStaleTabWarning } from 'Lib/stale-tab-warning';

import { usePods, useSetActivePod } from './Queries';

const PodsSelect = () => {
	const { toastSuccess, toastError } = useNotify();
	const history = useHistory();
	const location = useLocation();

	const [isPodStale, setIsPodStale] = useState(false);
	const [showPodStaleWarning, showPodStaleWarningHandlers] = useTrigger();
	const [previousPathname, setPreviousPathname] = useState(location.pathname);
	const [tabId] = useState(() => Math.random().toString(36).substring(2, 11));
	const [newPodID, setNewPodID] = useState(null);

	const selectedPortfolioGroupID = useSelector(state => state.app.selectedPortfolioGroupID);
	const portfolioGroupCount = useSelector(state => state.app.portfolioGroupCount);

	const { data: allPods, isLoading } = usePods();

	const filterOptions = (options, { inputValue }) => {
		const allPodsOption = options.find(option => option.groupID === null);
		const otherOptions = options.filter(option => option.groupID !== null);
		const filteredOtherOptions = otherOptions.filter(option => option.name.toLowerCase().includes(inputValue.toLowerCase()));

		return allPodsOption ? [allPodsOption, ...filteredOtherOptions] : filteredOtherOptions;
	};

	// Initialize stale tab warning
	useEffect(() => {
		initializeStaleTabWarning(selectedPortfolioGroupID);
	}, [selectedPortfolioGroupID]);

	useEffect(() => {
		if (isPodStale && location.pathname !== previousPathname) {
			window.location.href = location.pathname + location.search;
		}
		setPreviousPathname(location.pathname);
	}, [isPodStale, location, previousPathname]);

	useEffect(() => {
		const channel = new BroadcastChannel('pod-sync');
		const handlePodChange = event => {
			if (event.data.type === 'POD_CHANGED') {
				// Compare the broadcasted pod ID with current selection
				const broadcastedPodID = event.data.newPodID;
				const currentPodID = selectedPortfolioGroupID;

				// If they don't match, mark as stale
				if (broadcastedPodID !== currentPodID) {
					// Only show warnings if this tab is not the one making the pod change
					if (event.data.senderTabId !== tabId) {
						setNewPodID(broadcastedPodID);
						setIsPodStale(true);
						showPodStaleWarningHandlers.open();
					}
				} else {
					setIsPodStale(false);
					showPodStaleWarningHandlers.close();
				}
			}
		};

		channel.addEventListener('message', handlePodChange);

		return () => {
			channel.removeEventListener('message', handlePodChange);
			channel.close();
		};
	}, [selectedPortfolioGroupID]);

	const { mutate: setActivePod } = useSetActivePod((__, variables) => {
		setIsPodStale(false);

		const channel = new BroadcastChannel('pod-sync');
		channel.postMessage({
			type: 'POD_CHANGED',
			newPodID: variables.groupID,
			senderTabId: tabId,
			timestamp: Date.now()
		});
		channel.close();

		toastSuccess('Pod changed. Please wait for page to refresh.');
		setTimeout(() => {
			history.go(0);
		}, 1500);
	}, toastError);

	const dataCheck = allPods ? Object.values(allPods).filter(item => item && typeof item === 'object' && item.groupID) : [];

	const allPodsOption = { groupID: null, name: 'All pods' };
	const optionsWithAllPods = [allPodsOption, ...dataCheck];

	const selectedValue = selectedPortfolioGroupID ? dataCheck.find(option => String(option?.groupID) === String(selectedPortfolioGroupID)) || null : allPodsOption;

	return (
		portfolioGroupCount > 1 && (
			<>
				<Autocomplete
					label="Active Pod"
					disableClearable
					options={optionsWithAllPods}
					value={selectedValue}
					loading={isLoading}
					getOptionLabel={option => {
						return option?.name || '';
					}}
					isOptionEqualToValue={(option, value) => option?.groupID === value?.groupID}
					groupBy={() => 'Teams/Pods'}
					filterOptions={filterOptions}
					renderOption={(props, option) => {
						return (
							<Fragment key={option.groupID || 'all-pods'}>
								<li {...props}>{option.name}</li>
								{option.groupID === null && <Divider sx={{ my: 2 }} />}
							</Fragment>
						);
					}}
					renderInput={params => (
						<TextField
							{...params}
							label="Active Pod"
							placeholder="All pods"
							color={isPodStale ? 'warning' : 'primary'}
							InputProps={{
								...params.InputProps,
								startAdornment: (
									<InputAdornment position="start">
										<Icon sx={{ fontSize: 20, color: 'text.secondary' }}>search</Icon>
									</InputAdornment>
								)
							}}
							helperText={
								isPodStale ? (
									<Box
										component="span"
										sx={{
											display: 'flex',
											alignItems: 'start',
											gap: 1,
											color: 'common.darkOrange',
											fontSize: theme => theme.typography.pxToRem(14)
										}}>
										<Icon sx={{ color: 'common.darkOrange' }}>warning</Icon>
										This tab is showing {selectedValue?.name}. Current pod is{' '}
										{optionsWithAllPods.find(pod => String(pod?.groupID) === String(newPodID))?.name || 'All pods'}.
									</Box>
								) : (
									''
								)
							}
							FormHelperTextProps={{
								sx: isPodStale
									? {
											color: 'common.darkOrange',
											fontWeight: 'medium'
										}
									: {}
							}}
							sx={
								isPodStale
									? {
											'& .MuiOutlinedInput-root': {
												'& fieldset': {
													borderColor: 'common.darkOrange'
												},
												'&:hover fieldset': {
													borderColor: 'common.darkOrange'
												},
												'&.Mui-focused fieldset': {
													borderColor: 'common.darkOrange'
												}
											}
										}
									: {}
							}
						/>
					)}
					noOptionsText="No active pods found"
					onChange={(__, newValue) => {
						if (newValue?.groupID !== selectedPortfolioGroupID) {
							setActivePod({ groupID: newValue !== null ? newValue?.groupID : null });
						}
					}}
				/>
				<Dialog
					open={showPodStaleWarning}
					onClose={showPodStaleWarningHandlers.close}
					toggle={showPodStaleWarningHandlers.toggle}
					maxWidth="sm"
					fullWidth
					title="This tab isn't on the current pod."
					actions={() => (
						<>
							<Button variant="outlined" color="success" onClick={showPodStaleWarningHandlers.close}>
								Stay Here
							</Button>
							<Button
								variant="contained"
								color="success"
								onClick={() => {
									showPodStaleWarningHandlers.close();
									history.go(0);
								}}>
								Refresh and Switch
							</Button>
						</>
					)}>
					<DialogContent>
						<Typography>
							You switched to{' '}
							<Typography component="span" sx={{ fontWeight: 'fontWeightSemiBold' }}>
								{optionsWithAllPods.find(pod => String(pod?.groupID) === String(newPodID))?.name || 'All pods'}
							</Typography>{' '}
							in another tab. This tab is still on{' '}
							<Typography component="span" sx={{ fontWeight: 'fontWeightSemiBold' }}>
								{selectedValue?.name}
							</Typography>
							. Refresh to switch and update data. If you stay here, you can review info, but navigation will update this tab to the new pod.
						</Typography>
					</DialogContent>
				</Dialog>
			</>
		)
	);
};

export default PodsSelect;
